// =============================================================================
// msalConfig.js - NUCLEAR OPTION: Bulletproof MSAL Configuration
// =============================================================================
// This implementation uses multiple layers of validation and correction to
// ensure the authority URL is ALWAYS correct for Entra External ID user flows
// =============================================================================

import { LogLevel, PublicClientApplication } from '@azure/msal-browser';

// Centralized configuration utility
import { configLoader } from './configLoader';
import { centralizedConfig } from './centralizedConfig';
import { authLogger } from './authLogger';
import logger from './logger';
import { getAccountPrimaryLabel, getSwitcherEmailDisplay } from './msalAccountDisplay';

// ============================================================================
// AUTHORITY URL VALIDATOR & CONVERTER
// ============================================================================

/**
 * Deterministic tenant domain mapping
 * Maps environment to its corresponding tenant domain name
 */
const TENANT_DOMAIN_MAP = {
  dev: 'besperexternaldev.onmicrosoft.com',
  int: 'bespergabrielint.onmicrosoft.com', // Note: int uses bespergabrielint, not besperexternalint
  uat: 'besperexternaluat.onmicrosoft.com',
  prod: 'besperexternalprod.onmicrosoft.com',
};

const CIAM_DOMAIN_MAP = {
  dev: 'besperexternaldev.ciamlogin.com',
  int: 'bespergabrielint.ciamlogin.com', // Note: int uses bespergabrielint, not besperexternalint
  uat: 'besperexternaluat.ciamlogin.com',
  prod: 'besperexternalprod.ciamlogin.com',
};

/**
 * Regex patterns to detect incorrect authority URL formats
 */
const PATTERNS = {
  // Detects tenant ID (GUID format)
  TENANT_ID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  
  // Detects CIAM domain
  CIAM_DOMAIN: /\.ciamlogin\.com/,
  
  // Detects Microsoft tenant domain (.onmicrosoft.com)
  ONMICROSOFT_DOMAIN: /\.onmicrosoft\.com$/,
};

/**
 * Check if a URL segment is a tenant ID
 * @param {string} segment - URL segment to check
 * @returns {boolean} True if segment is a tenant ID
 */
function isTenantId(segment) {
  return PATTERNS.TENANT_ID.test(segment);
}

/**
 * Extract tenant identifier from authority URL
 * @param {string} authorityUrl - Full authority URL
 * @returns {string|null} Tenant identifier or null
 */
function extractTenantFromUrl(authorityUrl) {
  if (!authorityUrl) return null;
  
  try {
    const url = new URL(authorityUrl);
    const parts = url.pathname.split('/').filter(p => p.length > 0);
    
    // Authority format: https://domain/tenant/...
    if (parts.length > 0) {
      return parts[0];
    }
  } catch (e) {
    logger.error('MSAL-CONFIG', 'Failed to parse authority URL', { error: e.message });
  }
  
  return null;
}

/**
 * Determine correct tenant domain based on environment
 * @param {string} environment - Current environment (dev/prod)
 * @returns {object} { tenantDomain, ciamDomain, userFlow }
 */
function getTenantConfig(environment = 'prod') {
  const env = (environment || 'prod').toLowerCase();
  
  const tenantDomain = TENANT_DOMAIN_MAP[env];
  const ciamDomain = CIAM_DOMAIN_MAP[env];
  
  if (!tenantDomain || !ciamDomain) {
    logger.error('MSAL-CONFIG', 'Unknown environment', { environment: env });
    // Fallback to prod
    return {
      tenantDomain: TENANT_DOMAIN_MAP.prod,
      ciamDomain: CIAM_DOMAIN_MAP.prod,
      userFlow: 'b-esper_signup',
    };
  }
  
  return {
    tenantDomain,
    ciamDomain,
    userFlow: 'b-esper_signup',
  };
}

/**
 * Validate and correct authority URL for Entra External ID user flows
 * @param {string} authorityUrl - Authority URL to validate
 * @param {string} environment - Current environment
 * @returns {string} Corrected authority URL
 */
function validateAndCorrectAuthority(authorityUrl, environment = 'prod') {
  // Get correct tenant configuration
  const { tenantDomain, ciamDomain } = getTenantConfig(environment);
  const expectedAuthority = `https://${ciamDomain}/${tenantDomain}`;
  
  // If authority is empty or null, return correct format
  if (!authorityUrl) {
    logger.warn('MSAL-CONFIG', 'Authority URL was empty, using correct format', { expectedAuthority });
    return expectedAuthority;
  }
  
  // Extract tenant from provided authority (first path segment after domain)
  const currentTenant = extractTenantFromUrl(authorityUrl);
  
  // Check if current tenant is in wrong format (GUID/tenant ID)
  if (currentTenant && isTenantId(currentTenant)) {
    logger.error('MSAL-CONFIG', 'CRITICAL: Authority URL uses tenant ID instead of domain name', {
      provided: authorityUrl,
      detectedTenantId: currentTenant,
      correctingTo: expectedAuthority
    });
    return expectedAuthority;
  }
  
  // Verify the authority uses correct CIAM domain
  if (!authorityUrl.includes(ciamDomain)) {
    logger.warn('MSAL-CONFIG', 'Authority domain mismatch', {
      expectedDomain: ciamDomain,
      currentUrl: authorityUrl,
      correctingTo: expectedAuthority
    });
    return expectedAuthority;
  }
  
  // Verify the authority uses correct tenant domain
  // Note: This check uses includes() so it works even if user flow name is appended
  if (!authorityUrl.includes(tenantDomain)) {
    logger.warn('MSAL-CONFIG', 'Authority tenant mismatch', {
      expectedTenant: tenantDomain,
      currentUrl: authorityUrl,
      correctingTo: expectedAuthority
    });
    return expectedAuthority;
  }
  
  // Authority looks correct - preserve it as-is (including user flow name if present)
  // The buildMsalConfig function will append user flow name if not already present
  authLogger.debug('[MSAL Config] Authority URL is correct:', authorityUrl);
  return authorityUrl;
}

// ============================================================================
// CONFIGURATION LOADING
// ============================================================================

// Get configuration values (async)
let config = {};
let configLoaded = false;

// Load configuration asynchronously
const loadConfig = async () => {
  if (!configLoaded) {
    config = await configLoader.getAllAsync();
    configLoaded = true;
  }
  return config;
};

// ============================================================================
// MSAL CONFIGURATION BUILDER
// ============================================================================

/**
 * Build MSAL configuration with bulletproof authority URL handling
 * @returns {Promise<object>} MSAL configuration object
 */
async function buildMsalConfig() {
  authLogger.debug('[MSAL Config] Building MSAL configuration...');
  
  // Get configuration from centralizedConfig
  const currentConfig = await loadConfig();
  
  // Check if authentication is enabled
  if (currentConfig.ENABLE_AUTH !== 'true') {
    return null;
  }

  // Validate required configuration
  if (!currentConfig.ENTRA_CUSTOMER_CLIENT_ID) {
    authLogger.error('[MSAL Config] Missing ENTRA_CUSTOMER_CLIENT_ID');
    return null;
  }
  
  // Extract and validate authority URL
  let authorityUrl = currentConfig.ENTRA_AUTHORITY_URL;
  
  // Get environment (try from config, fallback to default)
  const environment = currentConfig.enviroment || 'prod';
  
  // No fallbacks: authority must be explicitly configured (env-config.js / local-config.js)
  if (!authorityUrl) {
    authLogger.error('[MSAL Config] Missing ENTRA_AUTHORITY_URL (required)');
    return null;
  }
  
  // Validate and correct authority URL (this will fix tenant ID format if present)
  authorityUrl = validateAndCorrectAuthority(authorityUrl, environment);
  
  // If invalid after correction, fail fast (no fallbacks)
  if (!authorityUrl) {
    authLogger.error('[MSAL Config] Authority URL invalid after validation');
    return null;
  }
  
  // CRITICAL: For Entra External ID (CIAM), the authority URL should NOT include the user flow name
  // The user flow name is passed as the 'p' parameter in authorize requests, not in the authority URL
  // The authority URL is used for OpenID discovery, which doesn't support flow names in the path
  // Format: https://{domain}.ciamlogin.com/{tenant-domain-name} (NO flow name here)
  const userFlowName = currentConfig.ENTRA_USER_FLOW_NAME;
  
  // Remove user flow name from authority if it's present (it shouldn't be)
  // This ensures OpenID discovery works correctly
  if (userFlowName && typeof userFlowName === 'string' && userFlowName.trim().length > 0) {
    const cleanAuthority = authorityUrl.replace(/\/+$/, '');
    if (cleanAuthority.endsWith(`/${userFlowName}`)) {
      // Remove the flow name from the authority URL
      authorityUrl = cleanAuthority.substring(0, cleanAuthority.length - userFlowName.length - 1);
      authLogger.log('[MSAL Config] Removed user flow from authority (flow will be passed as p parameter)', {
        originalAuthority: cleanAuthority,
        userFlow: userFlowName,
        finalAuthority: authorityUrl
      });
      authLogger.debug('[MSAL Config] Base authority (without flow)', { authorityUrl });
    } else {
      authLogger.debug('[MSAL Config] Authority does not include user flow (correct)');
      authLogger.debug('[MSAL Config] Base authority (correct format)', { authorityUrl });
    }
    
    // Store user flow name for use in authorize requests
    if (typeof window !== 'undefined') {
      window.__msal_user_flow_name = userFlowName;
    }
  } else {
    authLogger.warn('[MSAL Config] ENTRA_USER_FLOW_NAME not configured - user flow will not be used');
    logger.warn('MSAL-CONFIG', 'ENTRA_USER_FLOW_NAME not configured');
  }
  
  // Get known authority
  const knownAuthority = currentConfig.ENTRA_KNOWN_AUTHORITY || 
                         currentConfig.ENTRA_AUTHORITY_DOMAIN ||
                         CIAM_DOMAIN_MAP[environment] ||
                         CIAM_DOMAIN_MAP.prod;
  
  // =========================================================================
  // VALIDATION: Redirect URI must match exactly
  // IMPORTANT: Redirect URI must NOT include query parameters
  // Query parameters are handled after the redirect, not in the redirect URI itself
  // Azure AD app registration should have: http://localhost:3002/auth/callback (no query params)
  // =========================================================================
  const redirectUri = typeof window !== 'undefined' ? window.location.origin + '/auth/callback' : '/auth/callback';
  
  // Note: Region/provider query parameters are handled in the callback handler, not in the redirect URI
  // This ensures the redirect URI matches what's configured in Azure AD app registration
  // eslint-disable-next-line no-unused-vars
  const expectedRedirectUri = redirectUri;
  
  // Validate redirect URI matches current URL (if on callback page)
  if (window.location.pathname === '/auth/callback') {
    const currentUrl = window.location.origin + window.location.pathname;
    if (currentUrl !== redirectUri) {
      authLogger.warn('[MSAL Config] Redirect URI mismatch', {
        expected: redirectUri,
        current: currentUrl,
        difference: currentUrl !== redirectUri ? 'Path or origin mismatch' : 'none'
      });
    } else {
      authLogger.debug('[MSAL Config] Redirect URI matches current URL');
    }
  }
  
  // =========================================================================
  // VALIDATION: Authority consistency check
  // =========================================================================
  // Store authority for later comparison during callback
  if (typeof window !== 'undefined') {
    window.__msal_login_authority = authorityUrl;
    authLogger.debug('[MSAL Config] Stored login authority for consistency check');
  }
  
  // =========================================================================
  // VALIDATION: Ensure knownAuthorities includes all domains
  // =========================================================================
  const knownAuthoritiesList = [
    CIAM_DOMAIN_MAP.dev,
    CIAM_DOMAIN_MAP.int,
    CIAM_DOMAIN_MAP.uat,
    CIAM_DOMAIN_MAP.prod,
    knownAuthority, // Current domain from config
  ].filter((v, i, a) => a.indexOf(v) === i && v); // Remove duplicates and nulls
  
  authLogger.debug('[MSAL Config] KnownAuthorities configured', { count: knownAuthoritiesList.length });
  
  // Build MSAL config object
  // CRITICAL: The authority URL should NOT include the user flow name
  // The user flow name will be passed as the 'p' parameter in authorize requests
  // This allows OpenID discovery to work correctly (discovery endpoint doesn't support flow names)
  authLogger.debug('[MSAL Config] Final MSAL authority (base, without flow)', { authorityUrl });
  const msalConfig = {
    auth: {
      clientId: currentConfig.ENTRA_CUSTOMER_CLIENT_ID,
      authority: authorityUrl, // Base authority without flow name (used for OpenID discovery)
      redirectUri: redirectUri,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
      knownAuthorities: knownAuthoritiesList,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true, // CRITICAL: Store auth state in cookies to prevent loss during redirects
      // This ensures code verifier and other PKCE state persist across redirects
      // Using localStorage instead of sessionStorage fixes issues in Firefox and ensures
      // authentication state persists across browser sessions and tabs
      temporaryCacheLocation: 'localStorage'
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) {
            authLogger.error('[MSAL Error]', message);
          } else if (level === LogLevel.Warning) {
            authLogger.warn('[MSAL Warning]', message);
          }
          // Suppress Info and Verbose completely - only show errors and warnings
        },
        piiLoggingEnabled: false,
        logLevel: LogLevel.Warning
      },
      windowHashTimeout: 60000,
      allowNativeBroker: false,
      // Best Practice: Let MSAL handle OpenID Connect discovery automatically
      // MSAL will use the authority URL from config in the authorize request
      // even if it performs discovery internally for validation
    },
  };
  
  // Final validation
  authLogger.debug('[MSAL Config] Final configuration ready');
  
  return msalConfig;
}

// ============================================================================
// MSAL INSTANCE CREATION & CACHING
// ============================================================================

let msalInstance = null;

/**
 * Get or create MSAL PublicClientApplication instance
 * @returns {Promise<PublicClientApplication>} MSAL instance
 */
export async function getMsalInstance() {
  // Build config to get current authority
  const config = await buildMsalConfig();
  
  if (!config) {
    // Check if auth is disabled - if so, return null gracefully instead of throwing
    const currentConfig = await loadConfig();
    if (currentConfig.ENABLE_AUTH !== 'true') {
      authLogger.debug('[MSAL Config] Authentication is disabled, returning null MSAL instance');
      return null;
    }
    // If auth is enabled but config is null, that's an error
    throw new Error('MSAL configuration is null (authentication may be disabled)');
  }
  
  const configAuthority = config.auth.authority;
  
  // =========================================================================
  // VALIDATION: Authority consistency check
  // =========================================================================
  // Check if authority matches what was used during login (if available)
  if (typeof window !== 'undefined' && window.__msal_login_authority) {
    const loginAuthority = window.__msal_login_authority;
    
    // CRITICAL: Both loginAuthority and configAuthority should be base authorities (no flow name)
    // If either includes the flow name, normalize them for comparison
    const userFlowName = window._env_?.ENTRA_USER_FLOW_NAME || window.__msal_user_flow_name;
    let normalizedLoginAuthority = loginAuthority;
    let normalizedConfigAuthority = configAuthority;
    
    if (userFlowName) {
      // Remove flow name from loginAuthority if present
      if (normalizedLoginAuthority && normalizedLoginAuthority.endsWith(`/${userFlowName}`)) {
        normalizedLoginAuthority = normalizedLoginAuthority.substring(0, normalizedLoginAuthority.length - userFlowName.length - 1);
        logger.warn('MSAL-CONFIG', 'WARNING: window.__msal_login_authority included flow name (incorrect), normalized for comparison');
      }
      // Remove flow name from configAuthority if present (shouldn't happen, but check anyway)
      if (normalizedConfigAuthority && normalizedConfigAuthority.endsWith(`/${userFlowName}`)) {
        normalizedConfigAuthority = normalizedConfigAuthority.substring(0, normalizedConfigAuthority.length - userFlowName.length - 1);
        logger.error('MSAL-CONFIG', 'CRITICAL: configAuthority includes flow name (should not)');
      }
    }
    
    if (normalizedLoginAuthority !== normalizedConfigAuthority) {
      logger.warn('MSAL-CONFIG', 'WARNING: Authority mismatch detected', {
        loginAuthority: loginAuthority,
        normalizedLoginAuthority: normalizedLoginAuthority,
        callbackAuthority: configAuthority,
        normalizedConfigAuthority: normalizedConfigAuthority,
        mismatch: 'Authority used during login does not match callback authority'
      });
      logger.warn('MSAL-CONFIG', 'This can cause authentication failures - state may not match');
    } else {
      authLogger.debug('[MSAL Config] Authority consistency verified (base authority)', { authority: normalizedConfigAuthority });
    }
  }
  
  // Check if existing instance matches current config
  // Only recreate if authority actually changed - this prevents losing accounts after redirect
  if (msalInstance) {
    const instanceConfig = msalInstance.getConfiguration();
    const instanceAuthority = instanceConfig?.auth?.authority;
    
    // CRITICAL: Check if instance authority includes user flow name (should be base authority only)
    const userFlowName = window._env_?.ENTRA_USER_FLOW_NAME || window.__msal_user_flow_name;
    // eslint-disable-next-line no-unused-vars
    let instanceAuthorityBase = instanceAuthority;
    if (userFlowName && instanceAuthority && instanceAuthority.endsWith(`/${userFlowName}`)) {
      // Instance has flow-scoped authority - this is incorrect, need to recreate
      logger.warn('MSAL-CONFIG', 'CRITICAL: Existing instance has flow-scoped authority (incorrect)', {
        instanceAuthority,
        expectedBaseAuthority: configAuthority
      });
      logger.warn('MSAL-CONFIG', 'Recreating instance with correct base authority');
      msalInstance = null;
    } else if (instanceAuthority === configAuthority) {
      // Instance matches - reuse it (preserves accounts)
      authLogger.debug('[MSAL Config] Reusing existing MSAL instance (authority matches)');
      return msalInstance;
    } else {
      // Authority changed - need new instance
      logger.warn('MSAL-CONFIG', 'Authority changed, recreating instance', {
        oldAuthority: instanceAuthority,
        newAuthority: configAuthority
      });
      msalInstance = null;
    }
  }
  
  // Create new instance
  try {
    msalInstance = new PublicClientApplication(config);
    
    // CRITICAL: Verify the instance was created with the correct base authority
    const createdInstanceConfig = msalInstance.getConfiguration();
    const createdAuthority = createdInstanceConfig?.auth?.authority;
    
    // Check if created instance has flow-scoped authority (should not)
    const userFlowName = window._env_?.ENTRA_USER_FLOW_NAME || window.__msal_user_flow_name;
    if (userFlowName && createdAuthority && createdAuthority.endsWith(`/${userFlowName}`)) {
      logger.error('MSAL-CONFIG', 'CRITICAL: MSAL instance created with flow-scoped authority (incorrect)', {
        createdAuthority,
        expectedBaseAuthority: configAuthority
      });
      throw new Error('MSAL instance created with incorrect authority (includes user flow name). The authority should be the base authority without the flow name.');
    }
    
    authLogger.debug('[MSAL Config] MSAL instance created successfully');
    authLogger.debug('[MSAL Config] Instance authority (base, correct):', createdAuthority);
    authLogger.debug('[MSAL Config] User flow name (will be used in requests):', userFlowName || 'not configured');
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Failed to create MSAL instance', { error: error.message });
    throw error;
  }
  
  return msalInstance;
}

/**
 * Clear MSAL cache to ensure no stale configuration
 * Useful when authority URL changes or handling stuck interactions
 * 
 * NOTE: This is a utility function for edge cases. For normal logout,
 * use MSAL's logoutRedirect() method which handles cache clearing automatically.
 * 
 * This function preserves accounts to support fallback scenarios where
 * manually stored accounts might exist (from manual code exchange fallback).
 */
export function clearMsalCache() {
  authLogger.debug('[MSAL Config] Clearing MSAL cache...');
  
  try {
    // CRITICAL: Check for active interaction BEFORE clearing anything
    // This prevents state loss during OAuth redirect callback
    const interactionStatus = localStorage.getItem('msal.interaction.status');
    const interactionStart = localStorage.getItem('msal.interaction.start');
    let hasActiveInteraction = false;
    
    if (interactionStatus && interactionStatus !== 'None' && interactionStatus !== 'null') {
      if (interactionStart) {
        const interactionAge = Date.now() - parseInt(interactionStart, 10);
        // If interaction is less than 10 minutes old, consider it active
        if (interactionAge < 10 * 60 * 1000) {
          hasActiveInteraction = true;
          logger.warn('MSAL-CONFIG', 'Active interaction detected, preserving interaction state', {
            status: interactionStatus,
            age: `${Math.round(interactionAge / 1000)}s`
          });
        }
      } else {
        // If status exists but no start time, it might be a fresh interaction
        hasActiveInteraction = true;
        logger.warn('MSAL-CONFIG', 'Interaction status found without start time, preserving state to be safe');
      }
    }
    
    // CRITICAL: We now use localStorage, not sessionStorage
    // Clear old sessionStorage data to prevent conflicts (only if no active interaction)
    if (!hasActiveInteraction) {
      authLogger.debug('[MSAL Config] Clearing old sessionStorage MSAL data...');
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('msal') || key.includes('interaction')) {
          sessionStorage.removeItem(key);
          authLogger.debug(`  Removed from sessionStorage: ${key}`);
        }
      });
    } else {
      authLogger.debug('[MSAL Config] Skipping sessionStorage cleanup - active interaction detected');
    }
    
    // Preserve accounts that exist in MSAL's standard format in localStorage
    // This supports both normal MSAL accounts and fallback manually stored accounts
    const accountsKey = 'msal.account.keys';
    const accountKeysJson = localStorage.getItem(accountsKey);
    const accountKeys = accountKeysJson ? JSON.parse(accountKeysJson) : [];
    const preservedAccounts = {};
    const preservedTokens = {};
    const preservedInteractionState = {};
    
    // Preserve interaction state if active
    if (hasActiveInteraction) {
      Object.keys(localStorage).forEach(key => {
        if (key.includes('msal.interaction') || key.includes('msal.request')) {
          preservedInteractionState[key] = localStorage.getItem(key);
          logger.debug('MSAL-CONFIG', 'Preserving interaction state', { key });
        }
      });
    }
    
    // Store any existing accounts and their tokens before clearing
    if (accountKeys.length > 0) {
      authLogger.debug(`[MSAL Config] Found ${accountKeys.length} account(s) to preserve`);
      accountKeys.forEach(accountKey => {
        const accountData = localStorage.getItem(accountKey);
        if (accountData) {
          preservedAccounts[accountKey] = accountData;
          authLogger.debug(`  Preserving account: ${accountKey.substring(0, 60)}...`);
          
          // Also preserve tokens associated with this account
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(accountKey) && key !== accountKey) {
              preservedTokens[key] = localStorage.getItem(key);
            }
          });
        }
      });
    }
    
    // Clear localStorage MSAL keys (but preserve accounts and interaction state)
    Object.keys(localStorage).forEach(key => {
      if (key.includes('msal') || key.includes('interaction.status')) {
        // Don't remove preserved accounts or tokens
        if (preservedAccounts[key] || preservedTokens[key]) {
          return;
        }
        // Don't remove preserved interaction state
        if (preservedInteractionState[key]) {
          return;
        }
        // Preserve manual account marker
        if (key === 'msal.manual.account') {
          logger.debug('MSAL-CONFIG', 'Preserved manual account marker', { key });
          return;
        }
        localStorage.removeItem(key);
        logger.debug('MSAL-CONFIG', 'Removed from localStorage', { key });
      }
    });
    
    // Restore preserved accounts and tokens
    Object.entries(preservedAccounts).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    Object.entries(preservedTokens).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    
    // Restore account keys list if we had accounts
    if (accountKeys.length > 0) {
      localStorage.setItem(accountsKey, JSON.stringify(accountKeys));
      authLogger.debug(`[MSAL Config] Restored ${accountKeys.length} account(s) and associated tokens`);
    }
    
    authLogger.debug('[MSAL Config] MSAL cache cleared successfully');
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Failed to clear MSAL cache', { error: error.message });
  }
}

/**
 * Reset MSAL instance (force recreation)
 * Use this if authority URL changes at runtime
 */
export async function resetMsalInstance() {
  authLogger.debug('[MSAL Config] Resetting MSAL instance...');
  clearMsalCache();
  msalInstance = null;
  return getMsalInstance();
}

// ============================================================================
// COMPATIBILITY EXPORTS (for existing code)
// ============================================================================

/**
 * Create MSAL configuration (compatibility export)
 * @returns {Promise<object>} MSAL configuration object
 */
export const createMsalConfig = buildMsalConfig;

/**
 * Validate configuration (compatibility export)
 */
export const isMsalConfigValid = async () => {
  const currentConfig = await loadConfig();
  return !!(
    currentConfig.ENTRA_CUSTOMER_CLIENT_ID &&
    currentConfig.ENTRA_AUTHORITY_DOMAIN
  );
};

// ============================================================================
// PKCE HELPER FUNCTIONS
// ============================================================================

/**
 * Base64 URL encode (RFC 4648 §5)
 */
function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate PKCE code verifier and challenge
 */
// eslint-disable-next-line no-unused-vars
async function generatePKCE() {
  // Generate random 32-byte code verifier
  const codeVerifier = base64UrlEncode(crypto.getRandomValues(new Uint8Array(32)));
  
  // Generate code challenge (SHA256 hash of verifier)
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const codeChallenge = base64UrlEncode(hashBuffer);
  
  return { codeVerifier, codeChallenge };
}

// Login request configuration for Entra External ID
// Uses authorization code flow with PKCE (MSAL v3 default) which works correctly with user flows
export function getLoginRequest(instance, options = {}) {
  if (!instance) {
    throw new Error('MSAL instance is required to build login request');
  }
  
  const config = instance.getConfiguration();
  const authority = config?.auth?.authority;
  
  // Get user flow name for authorize requests
  const userFlowName = window._env_?.ENTRA_USER_FLOW_NAME || window.__msal_user_flow_name || 'b-esper_signup';
  
  // For Entra External ID, the authority should NOT include the flow name
  // The flow name is passed as the 'p' parameter in authorize requests
  if (authority) {
    authLogger.debug('[MSAL Config] Using base authority for login', { authority });
    authLogger.debug('[MSAL Config] User flow will be passed as p parameter', { userFlowName });
  }
  
  // Get API scope from multiple sources - include it in initial login to get consent upfront
  // This prevents the consent loop that happens when API scope is requested after login
  // Priority: window._env_ > centralizedConfig instance > process.env > fallback construction
  let apiScope = null;
  
  // Try window._env_ first (from env-config.js or centralizedConfig)
  if (window._env_ && window._env_.ENTRA_API_SCOPE) {
    apiScope = window._env_.ENTRA_API_SCOPE;
    authLogger.debug('[MSAL Config] Found API scope in window._env_:', apiScope);
  }
  
  // If not found, try to get from centralizedConfig instance (if available)
  if (!apiScope) {
    try {
      // Access the centralizedConfig singleton instance
      if (centralizedConfig && centralizedConfig.config && centralizedConfig.config.ENTRA_API_SCOPE) {
        apiScope = centralizedConfig.config.ENTRA_API_SCOPE;
        authLogger.debug('[MSAL Config] Found API scope in centralizedConfig:', apiScope);
      }
    } catch (e) {
      logger.warn('MSAL-CONFIG', 'Error accessing centralizedConfig', { error: e.message });
    }
  }
  
  // Fallback to process.env
  if (!apiScope) {
    apiScope = process.env.REACT_APP_ENTRA_API_SCOPE;
    if (apiScope) {
      authLogger.debug('[MSAL Config] Found API scope in process.env:', apiScope);
    }
  }
  
  // Last resort: construct from API app ID URI if available
  if (!apiScope && window._env_ && window._env_.ENTRA_API_APP_ID_URI) {
    const apiAppIdUri = window._env_.ENTRA_API_APP_ID_URI.trim();
    if (apiAppIdUri) {
      apiScope = `${apiAppIdUri.replace(/\/+$/, '')}/customer.read`;
      authLogger.debug('[MSAL Config] Constructed API scope from ENTRA_API_APP_ID_URI:', apiScope);
    }
  }
  
  // Build scopes array - always include basic OIDC scopes and offline_access for refresh tokens
  const scopes = ['openid', 'profile', 'email', 'offline_access'];
  
  // Add API scope if available - this ensures consent happens during login, not after
  if (apiScope && typeof apiScope === 'string' && apiScope.trim().length > 0) {
    scopes.push(apiScope);
    authLogger.debug('[MSAL Config] Including API scope in login request', { apiScope });
    authLogger.debug('[MSAL Config] Full scopes array', { scopes });
  } else {
    logger.error('MSAL-CONFIG', 'API scope not available during login - consent will be required later', {
      windowEnv: window._env_ ? 'present' : 'missing'
    });
    logger.error('MSAL-CONFIG', 'This will cause authentication loops');
  }
  
  // For Entra External ID, we need to pass the user flow name as the 'p' query parameter
  // CRITICAL: Do NOT use flow-scoped authority (path-based) as it breaks OpenID discovery
  // Instead, use the base authority and pass the flow name via extraQueryParameters
  // This allows MSAL to discover endpoints correctly while still targeting the correct user flow
  
  authLogger.debug('[MSAL Config] Using base authority for authorize request', { authority });
  authLogger.debug('[MSAL Config] User flow will be passed as p query parameter', { userFlowName });
  
  // Use base authority (for OpenID discovery) and pass flow name as query parameter
  // CRITICAL: Do NOT use prompt: 'login' - it forces re-authentication on every request
  // causing redirect loops. Let MSAL handle sessions naturally.
  const request = {
    scopes: scopes,
    // No prompt parameter by default - MSAL will use cached session if valid
    // Use 'select_account' only when explicitly adding a new account (passed via options)
    authority: authority, // Base authority (for OpenID discovery)
    extraQueryParameters: {
      p: userFlowName    // User flow name as query parameter (Entra External ID requirement)
    }
  };
  
  // Merge options into request (e.g., prompt: 'select_account' for adding accounts)
  if (options.prompt) {
    request.prompt = options.prompt;
    authLogger.debug('[MSAL Config] Including prompt parameter from options', { prompt: options.prompt });
  }
  
  // Merge any other options (like account hint, loginHint, etc.)
  if (options.account) {
    request.account = options.account;
  }
  if (options.loginHint) {
    request.loginHint = options.loginHint;
  }
  
  // CRITICAL: Verify extraQueryParameters is included
  authLogger.debug('[MSAL Config] Login request object includes extraQueryParameters', {
    hasExtraQueryParams: !!request.extraQueryParameters,
    pParam: request.extraQueryParameters?.p,
    fullExtraQueryParams: request.extraQueryParameters
  });
  
  return request;
}

// Sign up request configuration
export const signUpRequest = {
  scopes: ['openid', 'profile', 'email'],
  prompt: 'select_account'
};

// API token request configuration
// BEST PRACTICE: Only request API scope for access tokens (OIDC scopes are in ID token)
export const apiTokenRequest = (scopeFromEnv, instance = null) => {
  const scope = scopeFromEnv || (window._env_ && window._env_.ENTRA_API_SCOPE);
  if (!scope || !scope.includes('api://')) {
    logger.error('MSAL-CONFIG', 'No valid API scope provided to apiTokenRequest');
    return {
      scopes: [], // Empty scopes will cause MSAL to fail gracefully
      forceRefresh: false
    };
  }
  
  // For Entra External ID, we need to pass the user flow name as the 'p' query parameter
  // CRITICAL: Do NOT use flow-scoped authority (path-based) as it breaks OpenID discovery
  // Instead, use the base authority and pass the flow name via extraQueryParameters
  const userFlowName = window._env_?.ENTRA_USER_FLOW_NAME || window.__msal_user_flow_name || 'b-esper_signup';
  
  const request = {
    scopes: [scope], // Only API scope - OIDC scopes not needed for access tokens
    forceRefresh: false,
    extraQueryParameters: {
      p: userFlowName    // User flow name as query parameter (Entra External ID requirement)
    }
  };
  
  authLogger.debug('[MSAL Config] Using base authority for token request with p parameter', { userFlowName });
  
  return request;
};

// Check if using Entra External ID
export const isEntraExternalId = async () => {
  const currentConfig = await loadConfig();
  return currentConfig.ENTRA_AUTHORITY_DOMAIN?.includes('.ciamlogin.com');
};

// Get sign up URL for Entra External ID
/**
 * DEPRECATED: Manual URL construction - not following MSAL best practices
 * 
 * For sign-up, use authMethods.login() or authMethods.signUp() instead,
 * which use MSAL's standard loginRedirect() method.
 * 
 * This function is kept for backward compatibility only.
 * 
 * @deprecated Use authMethods.signUp(instance) instead
 */
export const getSignUpUrl = async () => {
  logger.warn('MSAL-CONFIG', 'getSignUpUrl() is deprecated. Use authMethods.signUp(instance) instead.');
  
  const currentConfig = await loadConfig();
  const isExternalId = await isEntraExternalId();
  
  if (!isExternalId) {
    return null;
  }
  
  const authorityUrl = currentConfig.ENTRA_AUTHORITY_URL;
  if (!authorityUrl) {
    logger.error('MSAL-CONFIG', 'Unable to build sign up URL - missing authority');
    return null;
  }
  
  // Manual URL construction - not recommended
  // Use MSAL's loginRedirect() instead for proper state management and PKCE
  return `${authorityUrl}/oauth2/v2.0/authorize?client_id=${currentConfig.ENTRA_CUSTOMER_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}&scope=openid profile email&prompt=select_account`;
};

// Authentication methods (keep existing implementation)
export const authMethods = {
  login: async (instance, options = {}) => {
    if (!instance) {
      logger.error('MSAL-CONFIG', 'MSAL instance is not available');
      return;
    }
    
    // Basic authority validation
    const instanceConfig = instance.getConfiguration();
    const instanceAuthority = instanceConfig?.auth?.authority;
    
    if (instanceAuthority) {
      const tenantIdPattern = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (tenantIdPattern.test(instanceAuthority)) {
        logger.error('MSAL-CONFIG', 'MSAL instance has wrong authority format (tenant ID instead of domain name)', { instanceAuthority });
        throw new Error('MSAL authority is incorrect. Please refresh the page.');
      }
      logger.debug('MSAL-CONFIG', 'Instance authority is correct', { instanceAuthority });
    }
    
    // BEST PRACTICE: Use MSAL's standard loginRedirect() method
    // =========================================================================
    // According to Microsoft best practices:
    // 1. Use loginRedirect() to initiate authentication - MSAL handles everything
    // 2. Don't call handleRedirectPromise() here - MsalProvider handles it on callback page
    // 3. Authorization code flow with PKCE works with Entra External ID user flows
    // 4. MSAL will use the authority URL from config in the authorize request
    // 5. Even if MSAL performs OpenID Connect discovery internally, the authorize URL
    //    should still use the tenant domain name from the authority config
    // =========================================================================
    
    // BEST PRACTICE: MSAL handles interaction_in_progress errors automatically
    // We don't need to check interaction status manually - MSAL will throw
    // InteractionInProgressError if an interaction is already in progress,
    // and we handle that in the catch block below
    
    // =========================================================================
    // VALIDATION: Redirect URI must match Azure AD app registration exactly
    // Azure does not allow query params in redirect URIs; region/provider are
    // stored in localStorage and read in the callback, not in the redirect URI.
    // =========================================================================
    const configRedirectUri = instanceConfig?.auth?.redirectUri;
    const expectedRedirectUri = typeof window !== 'undefined' ? window.location.origin + '/auth/callback' : '/auth/callback';
    if (configRedirectUri !== expectedRedirectUri) {
      logger.warn('MSAL-CONFIG', 'WARNING: Redirect URI mismatch', {
        configRedirectUri,
        expectedRedirectUri,
        currentOrigin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
        currentPathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
      });
      logger.warn('MSAL-CONFIG', 'This can cause authentication failures - redirect URI must match exactly');
    } else {
      logger.debug('MSAL-CONFIG', 'Redirect URI validated', { configRedirectUri });
    }
    
    // Log client ID and redirect URI that will be used
    logger.debug('MSAL-CONFIG', 'Client ID being used', {
      clientId: instanceConfig?.auth?.clientId?.substring(0, 8) + '...',
      fullClientId: instanceConfig?.auth?.clientId,
      authority: instanceConfig?.auth?.authority,
      redirectUri: instanceConfig?.auth?.redirectUri
    });
    
    // Log redirect URI that will be used
    logger.debug('MSAL-CONFIG', 'Redirect URI Configuration', {
      redirectUri: configRedirectUri,
      willBeUsed: 'This URI must match exactly with Azure AD app registration'
    });
    
    try {
      logger.info('MSAL-CONFIG', 'Starting login flow with MSAL loginRedirect() (BEST PRACTICE)');
      logger.debug('MSAL-CONFIG', 'MSAL will handle: state management, PKCE, token exchange automatically');
      logger.debug('MSAL-CONFIG', 'User will be redirected to Azure AD for authentication');
      logger.debug('MSAL-CONFIG', 'After authentication, user will be redirected back', { configRedirectUri });
      
      // BEST PRACTICE: MSAL instance should already be initialized before use
      // The instance is initialized in index.js before being passed to MsalProvider
      // If for some reason it's not initialized, MSAL will handle it automatically
      // No need to check or call initialize() here - MSAL handles it internally
      
      const loginRequest = getLoginRequest(instance, options);
      logger.debug('MSAL-CONFIG', 'Login request configuration', {
        scopes: loginRequest.scopes,
        prompt: loginRequest.prompt,
        authority: loginRequest.authority,
        redirectUri: configRedirectUri,
        extraQueryParameters: loginRequest.extraQueryParameters,
        pParameter: loginRequest.extraQueryParameters?.p
      });
      
      // CRITICAL: Verify p parameter is present before redirect
      if (!loginRequest.extraQueryParameters?.p) {
        logger.error('MSAL-CONFIG', 'CRITICAL: p parameter (user flow) missing from login request', {
          extraQueryParams: loginRequest.extraQueryParameters,
          userFlowName: window._env_?.ENTRA_USER_FLOW_NAME || window.__msal_user_flow_name
        });
        throw new Error('User flow name (p parameter) missing from login request');
      }
      
      // Use MSAL's standard method - it will handle everything automatically
      // This is the recommended approach per Microsoft best practices
      logger.debug('MSAL-CONFIG', 'Calling instance.loginRedirect() with p parameter', { p: loginRequest.extraQueryParameters.p });
      await instance.loginRedirect(loginRequest);
      logger.debug('MSAL-CONFIG', 'loginRedirect() call completed (redirect should have happened)');
      
    } catch (error) {
      if (error.errorCode === 'interaction_in_progress') {
        // User explicitly requested login (e.g. clicked "Sign in again") but MSAL thinks an interaction is in progress.
        // Clear stuck state and retry once so the redirect can proceed.
        logger.warn('MSAL-CONFIG', 'interaction_in_progress on loginRedirect, clearing stuck state and retrying once');
        authMethods.clearStuckAuth(instance);
        await new Promise((r) => setTimeout(r, 200));
        try {
          const retryRequest = getLoginRequest(instance, options);
          await instance.loginRedirect(retryRequest);
        } catch (retryError) {
          logger.error('MSAL-CONFIG', 'Login retry after clear failed', { error: retryError.message });
          throw retryError;
        }
        return;
      } else {
        logger.error('MSAL-CONFIG', 'Login failed', { error: error.message });
        throw error;
      }
    }
  },

  signUp: async (instance) => {
    // For Entra External ID, sign-up uses the same flow as login
    return authMethods.login(instance);
  },

  acquireToken: async (instance, scopesOverride) => {
    if (!instance) {
      logger.error('MSAL-CONFIG', 'MSAL instance is not available');
      return null;
    }

    // Use centralized active account helper
    const account = getOrSelectActiveAccount(instance);
    if (!account) {
      logger.error('MSAL-CONFIG', 'No active account found');
      return null;
    }

    // BEST PRACTICE: Only request API scope for access tokens
    // OIDC scopes are already in ID token from login
    const scope = scopesOverride || (window._env_ && window._env_.ENTRA_API_SCOPE);
    if (!scope || !scope.includes('api://')) {
      logger.error('MSAL-CONFIG', 'No valid API scope found - cannot acquire access token');
      return null;
    }
    
    try {
      const tokenRequest = apiTokenRequest(scope);
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: account
      });
      return response.accessToken; // Always return access token for API calls
    } catch (error) {
      logger.error('MSAL-CONFIG', 'Failed to acquire token silently', { error: error.message });
      return null;
    }
  },

  logout: (instance) => {
    if (!instance) {
      logger.error('MSAL-CONFIG', 'MSAL instance is not available');
      return;
    }
    import('./wsClient').then(({ disconnectWs }) => { disconnectWs(); }).catch(() => {});
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin }).catch((error) => {
      logger.error('MSAL-CONFIG', 'Logout failed', { error: error.message });
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = window.location.origin;
    });
  },

  clearStuckAuth: (instance) => {
    if (!instance) {
      logger.error('MSAL-CONFIG', 'MSAL instance is not available');
      return;
    }
    try {
      instance.clearCache();
      sessionStorage.setItem('msal_clear_interaction', 'true');
      localStorage.removeItem('msal.interaction.status');
      localStorage.removeItem('msal.interaction.error');
    } catch (error) {
      logger.error('MSAL-CONFIG', 'Failed to clear authentication state', { error: error.message });
    }
  }
};

// ============================================================================
// ACTIVE ACCOUNT MANAGEMENT (Multi-Account Support)
// ============================================================================

/**
 * Storage key for active account selection (keep in sync with platform-auth activeAccount.js).
 */
export const ACTIVE_ACCOUNT_STORAGE_KEY = 'baf.activeAccount.homeAccountId';

/**
 * Get or select the active account deterministically
 * This ensures we always have exactly one active account, even when multiple accounts exist
 * 
 * @param {PublicClientApplication} instance - MSAL instance
 * @returns {AccountInfo|null} Active account or null if none available
 */
export function getOrSelectActiveAccount(instance) {
  if (!instance) {
    logger.warn('MSAL-CONFIG', 'No MSAL instance provided');
    return null;
  }

  try {
    const allAccounts = instance.getAllAccounts() || [];
    let activeAccount = null;
    try {
      activeAccount = instance.getActiveAccount();
    } catch (_) {
      activeAccount = null;
    }

    // Multi-account: MSAL may pick a default active account on reload; persisted selection must win.
    if (allAccounts.length > 1) {
      const storedHomeAccountId = localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
      if (storedHomeAccountId) {
        const storedAccount = allAccounts.find((acc) => acc.homeAccountId === storedHomeAccountId);
        if (storedAccount) {
          if (!activeAccount || activeAccount.homeAccountId !== storedHomeAccountId) {
            try {
              instance.setActiveAccount(storedAccount);
            } catch (e) {
              logger.warn('MSAL-CONFIG', 'Failed to apply stored active account over MSAL default', {
                error: e.message,
              });
            }
            authLogger.debug('[ActiveAccount] Using persisted selection (multi-account)', {
              username: storedAccount.username || storedAccount.name,
              homeAccountId: storedAccount.homeAccountId,
            });
          }
          return storedAccount;
        }
        localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
      }
    }

    if (activeAccount) {
      authLogger.debug('[ActiveAccount] Using getActiveAccount()', {
        username: activeAccount.username || activeAccount.name,
        homeAccountId: activeAccount.homeAccountId,
      });
      return activeAccount;
    }

    if (allAccounts.length === 0) {
      authLogger.debug('[ActiveAccount] No accounts available');
      return null;
    }

    if (allAccounts.length === 1) {
      // BEST PRACTICE: For single account, set it as active and persist
      // This ensures consistent account selection
      const account = allAccounts[0];
      try {
        instance.setActiveAccount(account);
        localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, account.homeAccountId);
        authLogger.debug('[ActiveAccount] Single account found, set as active', {
          username: account.username || account.name,
          homeAccountId: account.homeAccountId
        });
      } catch (e) {
        logger.warn('MSAL-CONFIG', 'Failed to set single account as active', { error: e.message });
      }
      return account;
    }

    // Step 4: Multiple accounts - check stored selection
    const storedHomeAccountId = localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
    if (storedHomeAccountId) {
      const storedAccount = allAccounts.find(acc => acc.homeAccountId === storedHomeAccountId);
      if (storedAccount) {
        try {
          instance.setActiveAccount(storedAccount);
          authLogger.debug('[ActiveAccount] Using stored active account', {
            username: storedAccount.username || storedAccount.name,
            homeAccountId: storedAccount.homeAccountId
          });
          return storedAccount;
        } catch (e) {
          logger.warn('MSAL-CONFIG', 'Failed to set stored active account', { error: e.message });
        }
      } else {
        // Stored account no longer exists - clear it
        logger.warn('MSAL-CONFIG', 'Stored account no longer exists, clearing selection');
        localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
      }
    }

    // Step 5: No stored selection - prefer account matching current authority
    const config = instance.getConfiguration();
    const currentAuthority = config?.auth?.authority;
    const authorityTenant = currentAuthority?.includes('/') 
      ? currentAuthority.split('/').pop() 
      : null;

    if (currentAuthority && authorityTenant) {
      const matchingAccount = allAccounts.find(acc => {
        const accountTenant = acc.tenantId;
        const authorityDomain = currentAuthority.match(/https?:\/\/([^/]+)/)?.[1];
        const accountEnvironment = acc.environment || '';
        
        return accountTenant === authorityTenant || 
               currentAuthority.includes(accountTenant) ||
               currentAuthority.includes(accountEnvironment) ||
               (authorityDomain && accountEnvironment && authorityDomain.includes(accountEnvironment));
      });

      if (matchingAccount) {
        try {
          instance.setActiveAccount(matchingAccount);
          localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, matchingAccount.homeAccountId);
          authLogger.debug('[ActiveAccount] Using account matching current authority', {
            username: matchingAccount.username || matchingAccount.name,
            homeAccountId: matchingAccount.homeAccountId,
            tenantId: matchingAccount.tenantId
          });
          return matchingAccount;
        } catch (e) {
          logger.warn('MSAL-CONFIG', 'Failed to set matching account', { error: e.message });
        }
      }
    }

    // Step 6: Fallback to first account (but don't set as active - user must choose)
    logger.warn('MSAL-CONFIG', 'Multiple accounts found but no active selection. User must choose', {
      accountCount: allAccounts.length,
      accounts: allAccounts.map(a => ({
        username: a.username || a.name,
        homeAccountId: a.homeAccountId,
        tenantId: a.tenantId
      }))
    });
    
    // Return null to force user selection
    return null;
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Error getting/selecting active account', { error: error.message });
    return null;
  }
}

/**
 * Set the active account and persist selection
 * 
 * @param {PublicClientApplication} instance - MSAL instance
 * @param {AccountInfo} account - Account to set as active
 * @returns {boolean} True if successful
 */
export function setActiveAccountPersistent(instance, account) {
  if (!instance || !account) {
    logger.warn('MSAL-CONFIG', 'Cannot set active account - missing instance or account');
    return false;
  }

  try {
  instance.setActiveAccount(account);
  localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, account.homeAccountId);
  authLogger.debug('[ActiveAccount] Set and persisted active account', {
    username: account.username || account.name,
    homeAccountId: account.homeAccountId
    });
    return true;
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Failed to set active account', { error: error.message });
    return false;
  }
}

/**
 * Clear the active account selection
 * 
 * @param {PublicClientApplication} instance - MSAL instance
 */
export function clearActiveAccountSelection(instance) {
  if (instance) {
    try {
      instance.setActiveAccount(null);
    } catch (e) {
      logger.warn('MSAL-CONFIG', 'Failed to clear active account', { error: e.message });
    }
  }
  localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
  authLogger.debug('[ActiveAccount] Cleared active account selection');
}

/**
 * Get all cached accounts
 * 
 * @param {PublicClientApplication} instance - MSAL instance
 * @returns {AccountInfo[]} Array of all cached accounts
 */
export function getAllCachedAccounts(instance) {
  if (!instance) {
    return [];
  }
  try {
    return instance.getAllAccounts() || [];
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Error getting all accounts', { error: error.message });
    return [];
  }
}

// ============================================================================
// ACCOUNT HISTORY MANAGEMENT
// ============================================================================

const ACCOUNT_HISTORY_KEY = 'account_history';
const MAX_HISTORY_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

/**
 * Save account to persistent history (even after tokens expire)
 * @param {AccountInfo} account - MSAL account object
 */
export function saveAccountToHistory(account) {
  if (!account || !account.homeAccountId) {
    return;
  }

  try {
    let history = [];
    const stored = localStorage.getItem(ACCOUNT_HISTORY_KEY);
    if (stored) {
      try {
        history = JSON.parse(stored);
      } catch (e) {
        logger.warn('MSAL-CONFIG', 'Failed to parse stored history, starting fresh', { error: e.message });
        history = [];
      }
    }

    // Remove old entries (older than MAX_HISTORY_AGE_MS)
    const now = Date.now();
    history = history.filter(entry => (now - entry.lastLogin) < MAX_HISTORY_AGE_MS);

    // Check if account already exists in history
    const existingIndex = history.findIndex(
      entry => entry.homeAccountId === account.homeAccountId
    );

    const prev = existingIndex >= 0 ? history[existingIndex] : null;
    const freshSignInEmail = getSwitcherEmailDisplay(account);
    const usernameStr = typeof account.username === 'string' ? account.username.trim() : '';
    const lastKnownSignInEmail =
      (freshSignInEmail && freshSignInEmail.trim()) ||
      (prev && typeof prev.lastKnownSignInEmail === 'string' && prev.lastKnownSignInEmail.includes('@')
        ? prev.lastKnownSignInEmail.trim()
        : '') ||
      (usernameStr.includes('@') ? usernameStr : '') ||
      (prev && typeof prev.username === 'string' && prev.username.includes('@') ? prev.username.trim() : '') ||
      '';

    const accountEntry = {
      username: account.username || account.name || '',
      name: getAccountPrimaryLabel(account),
      homeAccountId: account.homeAccountId,
      localAccountId: account.localAccountId || '',
      tenantId: account.tenantId || '',
      lastLogin: now,
      environment: account.environment || '',
      lastKnownSignInEmail,
    };

    if (existingIndex >= 0) {
      // Update existing entry
      history[existingIndex] = accountEntry;
    } else {
      // Add new entry
      history.push(accountEntry);
    }

    // Sort by lastLogin (most recent first)
    history.sort((a, b) => b.lastLogin - a.lastLogin);

    localStorage.setItem(ACCOUNT_HISTORY_KEY, JSON.stringify(history));
    logger.debug('MSAL-CONFIG', 'Saved account to history', { accountName: accountEntry.name });
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Error saving account to history', { error: error.message });
  }
}

/**
 * Get all accounts from history (including expired ones)
 * @returns {Array} Array of account history entries
 */
export function getAccountHistory() {
  try {
    const stored = localStorage.getItem(ACCOUNT_HISTORY_KEY);
    if (!stored) {
      return [];
    }

    const history = JSON.parse(stored);
    const now = Date.now();

    // Filter out entries older than MAX_HISTORY_AGE_MS
    const validHistory = history.filter(
      entry => (now - entry.lastLogin) < MAX_HISTORY_AGE_MS
    );

    // If we filtered out entries, save the cleaned history
    if (validHistory.length !== history.length) {
      localStorage.setItem(ACCOUNT_HISTORY_KEY, JSON.stringify(validHistory));
    }

    return validHistory;
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Error getting account history', { error: error.message });
    return [];
  }
}

/**
 * homeAccountId → last known @ sign-in id (UPN/email) when MSAL cache omits idTokenClaims.
 * @returns {Record<string, string>}
 */
export function getHistorySignInEmailByHomeAccountId() {
  const out = {};
  for (const e of getAccountHistory()) {
    if (!e || typeof e.homeAccountId !== 'string' || !e.homeAccountId) continue;
    const fromStored =
      typeof e.lastKnownSignInEmail === 'string' && e.lastKnownSignInEmail.includes('@')
        ? e.lastKnownSignInEmail.trim()
        : '';
    const fromUser = typeof e.username === 'string' && e.username.includes('@') ? e.username.trim() : '';
    const v = fromStored || fromUser;
    if (v) out[e.homeAccountId] = v;
  }
  return out;
}

/**
 * Remove account from history
 * @param {string} homeAccountId - Account's homeAccountId
 */
export function removeFromHistory(homeAccountId) {
  if (!homeAccountId) {
    return;
  }

  try {
    const history = getAccountHistory();
    const filtered = history.filter(
      entry => entry.homeAccountId !== homeAccountId
    );

    if (filtered.length !== history.length) {
      localStorage.setItem(ACCOUNT_HISTORY_KEY, JSON.stringify(filtered));
      logger.debug('MSAL-CONFIG', 'Removed account from history', { homeAccountId });
    }
  } catch (error) {
    logger.error('MSAL-CONFIG', 'Error removing account from history', { error: error.message });
  }
}

/**
 * Get all accounts (cached + historical) for display
 * @param {PublicClientApplication} instance - MSAL instance
 * @returns {Object} { cached: AccountInfo[], historical: Array }
 */
export function getAllAccountsWithHistory(instance) {
  const cached = getAllCachedAccounts(instance);
  const historical = getAccountHistory();

  // Filter out historical accounts that are already in cache
  const cachedIds = new Set(cached.map(acc => acc.homeAccountId));
  const historicalOnly = historical.filter(
    entry => !cachedIds.has(entry.homeAccountId)
  );

  return {
    cached,
    historical: historicalOnly
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export { validateAndCorrectAuthority, getTenantConfig };
